import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useUIPrefs, type HideableSection } from './UIPrefsContext';

export interface TourStep {
  id: string;
  targetId: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  route?: string;
}

function buildTourSteps(
  role: string | undefined,
  isSectionHidden: (section: HideableSection) => boolean
): TourStep[] {
  const isAdmin = role === 'superadmin' || role === 'beheerder' || role === 'groepsbeheerder';
  const isUserAdmin = role === 'superadmin' || role === 'beheerder';

  const steps: TourStep[] = [
    {
      id: 'dashboard',
      targetId: 'nav-dashboard',
      title: 'Dashboard',
      description: 'Hier zie je een overzicht van je diensten, teams en openstaande verzoeken.',
      position: 'right',
      route: '/',
    },
    {
      id: 'mijn-rooster',
      targetId: 'nav-mijn-rooster',
      title: 'Mijn Rooster',
      description: 'Bekijk hier wanneer je bent ingepland. Je ziet al je komende diensten in een handig overzicht.',
      position: 'right',
      route: '/mijn-rooster',
    },
    {
      id: 'beschikbaarheid',
      targetId: 'nav-beschikbaarheid',
      title: 'Beschikbaarheid',
      description: 'Geef hier aan wanneer je wel of niet kunt. Zo voorkom je dat je wordt ingepland als het niet uitkomt.',
      position: 'right',
      route: '/beschikbaarheid',
    },
    {
      id: 'ruilverzoeken',
      targetId: 'nav-ruilverzoeken',
      title: 'Ruilverzoeken',
      description: 'Kun je een keer niet? Vraag hier iemand anders om te ruilen. Je kunt ook verzoeken van anderen accepteren.',
      position: 'right',
      route: '/ruilverzoeken',
    },
    {
      id: 'planning',
      targetId: 'nav-planning',
      title: 'Planning',
      description: isAdmin
        ? 'Maak en beheer hier het rooster: diensten aanmaken, vrijwilligers inplannen en het rooster publiceren.'
        : 'Bekijk hier het volledige rooster van alle teams en diensten.',
      position: 'right',
      route: '/planning',
    },
  ];

  if (!isSectionHidden('vrije-diensten')) {
    steps.push({
      id: 'vrije-diensten',
      targetId: 'nav-vrije-diensten',
      title: 'Vrije diensten',
      description: 'Diensten zonder vrijwilliger? Hier zie je openstaande diensten en kun je jezelf aanmelden.',
      position: 'right',
      route: '/vrije-diensten',
    });
  }

  steps.push({
    id: 'berichten',
    targetId: 'nav-berichten',
    title: 'Berichten',
    description: 'Lees hier mededelingen en berichten van je team en gemeente.',
    position: 'right',
    route: '/berichten',
  });

  if (isAdmin) {
    steps.push(
      {
        id: 'coordinator',
        targetId: 'nav-coordinator',
        title: 'Coordinator',
        description: 'Snel overzicht voor coordinatoren: bezetting, openstaande verzoeken en aandachtspunten in een oogopslag.',
        position: 'right',
        route: '/coordinator',
      },
      {
        id: 'teams',
        targetId: 'nav-teams',
        title: 'Teams',
        description: 'Beheer je teams: leden toevoegen, rollen toewijzen en subgroepen of roulatie instellen.',
        position: 'right',
        route: '/teams',
      }
    );

    if (!isSectionHidden('rapportages')) {
      steps.push({
        id: 'rapportages',
        targetId: 'nav-rapportages',
        title: 'Rapportages',
        description: 'Inzicht in de bezetting en inzet van vrijwilligers, met grafieken en exports.',
        position: 'right',
        route: '/rapportages',
      });
    }
  }

  if (isUserAdmin) {
    steps.push({
      id: 'gebruikers',
      targetId: 'nav-gebruikers',
      title: 'Gebruikers',
      description: 'Beheer alle gebruikers: accounts aanmaken, rollen wijzigen en gebruikers verwijderen.',
      position: 'right',
      route: '/gebruikers',
    });
  }

  steps.push({
    id: 'instellingen',
    targetId: 'nav-instellingen',
    title: 'Instellingen',
    description: 'Pas hier je profiel, thema en meldingen aan. Je kunt de rondleiding hier ook altijd opnieuw starten.',
    position: 'right',
    route: '/instellingen',
  });

  return steps;
}

const TUTORIAL_STORAGE_KEY = 'kerkplanner_tutorial_state';
const BADGES_STORAGE_KEY = 'kerkplanner_badges_dismissed';

interface TutorialState {
  tourCompleted: boolean;
  tourActive: boolean;
  currentStep: number;
  badgesDismissed: string[];
}

interface TutorialContextType {
  tourActive: boolean;
  currentStep: number;
  tourCompleted: boolean;
  badgesDismissed: string[];
  showBadge: (navId: string) => boolean;
  startTour: () => void;
  stopTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  dismissBadge: (navId: string) => void;
  dismissAllBadges: () => void;
  resetTutorial: () => void;
  currentTourStep: TourStep | null;
  tourSteps: TourStep[];
}

const TutorialContext = createContext<TutorialContextType | null>(null);

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
}

function loadState(): TutorialState {
  try {
    const stored = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    const badges = localStorage.getItem(BADGES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        badgesDismissed: badges ? JSON.parse(badges) : [],
        tourActive: false,
      };
    }
  } catch {
    // ignore
  }
  return {
    tourCompleted: false,
    tourActive: false,
    currentStep: 0,
    badgesDismissed: [],
  };
}

function saveState(state: Partial<TutorialState>) {
  try {
    const current = loadState();
    localStorage.setItem(
      TUTORIAL_STORAGE_KEY,
      JSON.stringify({ ...current, ...state, tourActive: false })
    );
    if (state.badgesDismissed !== undefined) {
      localStorage.setItem(BADGES_STORAGE_KEY, JSON.stringify(state.badgesDismissed));
    }
  } catch {
    // ignore
  }
}

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TutorialState>(loadState);
  const { profile } = useAuth();
  const { isSectionHidden } = useUIPrefs();

  const tourSteps = useMemo(
    () => buildTourSteps(profile?.role, isSectionHidden),
    [profile?.role, isSectionHidden]
  );

  useEffect(() => {
    saveState(state);
  }, [state.tourCompleted, state.badgesDismissed]);

  const showBadge = useCallback(
    (navId: string) => {
      if (state.tourCompleted || state.tourActive) return false;
      return !state.badgesDismissed.includes(navId);
    },
    [state.tourCompleted, state.tourActive, state.badgesDismissed]
  );

  const startTour = useCallback(() => {
    setState((prev) => ({ ...prev, tourActive: true, currentStep: 0 }));
  }, []);

  const stopTour = useCallback(() => {
    setState((prev) => ({ ...prev, tourActive: false }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => {
      const next = prev.currentStep + 1;
      if (next >= tourSteps.length) {
        return { ...prev, tourActive: false, tourCompleted: true };
      }
      return { ...prev, currentStep: next };
    });
  }, [tourSteps.length]);

  const prevStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  }, []);

  const skipTour = useCallback(() => {
    setState((prev) => ({ ...prev, tourActive: false, tourCompleted: true }));
  }, []);

  const dismissBadge = useCallback((navId: string) => {
    setState((prev) => {
      if (prev.badgesDismissed.includes(navId)) return prev;
      return {
        ...prev,
        badgesDismissed: [...prev.badgesDismissed, navId],
      };
    });
  }, []);

  const dismissAllBadges = useCallback(() => {
    const allIds = tourSteps.map((s) => s.targetId);
    setState((prev) => ({ ...prev, badgesDismissed: allIds }));
  }, [tourSteps]);

  const resetTutorial = useCallback(() => {
    setState({
      tourCompleted: false,
      tourActive: false,
      currentStep: 0,
      badgesDismissed: [],
    });
    localStorage.removeItem(TUTORIAL_STORAGE_KEY);
    localStorage.removeItem(BADGES_STORAGE_KEY);
  }, []);

  const currentTourStep = state.tourActive
    ? tourSteps[Math.min(state.currentStep, tourSteps.length - 1)]
    : null;

  return (
    <TutorialContext.Provider
      value={{
        tourActive: state.tourActive,
        currentStep: state.currentStep,
        tourCompleted: state.tourCompleted,
        badgesDismissed: state.badgesDismissed,
        showBadge,
        startTour,
        stopTour,
        nextStep,
        prevStep,
        skipTour,
        dismissBadge,
        dismissAllBadges,
        resetTutorial,
        currentTourStep,
        tourSteps,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}
